import { GreyChipButton } from '@/components/ui/generic/grey-chip-button';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import { Kbd } from '@/components/ui/kbd';
import { useVehiclesContext } from '@/contexts/vehicles-provider';
import getOS from '@/lib/get-operating-system';
import { createFileRoute, redirect, useLocation } from '@tanstack/react-router';
import {
  BanknoteArrowDown,
  CircleDotDashed,
  GalleryHorizontalEnd,
  SearchIcon,
  TreePalm,
} from 'lucide-react';
import { useMemo } from 'react';
import { KeepAliveOutlet } from 'tanstack-router-keepalive';

export const Route = createFileRoute('/(authenticated)/cars')({
  beforeLoad: ({ location }) => {
    // If someone visits /cars directly, redirect to /cars/all
    if (location.pathname === '/cars') {
      throw redirect({
        to: '/cars/all',
      });
    }
  },
  component: RouteComponent,
  staticData: {
    keepAlive: true,
  },
});

const filterChips = [
  {
    label: 'All listings',
    icon: GalleryHorizontalEnd,
    href: '/cars/all',
  },
  {
    label: 'Available',
    icon: TreePalm,
    href: '/cars/available',
  },
  {
    label: 'Purchase in Progress',
    icon: CircleDotDashed,
    href: '/cars/pending',
  },
  {
    label: 'Sold',
    icon: BanknoteArrowDown,
    href: '/cars/sold',
  },
];

function RouteComponent() {
  const pathname = useLocation({
    select: (location) => location.pathname,
  });
  const { searchQuery, setSearchQuery, setFocusSearchInput, searchInputRef } =
    useVehiclesContext();

  const os = getOS();
  const modifierKey = useMemo(() => {
    return os === 'mac' ? '⌘' : 'Ctrl';
  }, [os]);

  // Check if we're on a listing detail page (matches pattern /cars/{id})
  const isListingDetailPage =
    /^\/cars\/[^\/]+\/?$/.test(pathname) &&
    !['/cars/all', '/cars/available', '/cars/pending', '/cars/sold'].includes(
      pathname,
    );

  // For listing detail pages, just render the outlet without the filter layout
  if (isListingDetailPage) {
    return <KeepAliveOutlet />;
  }

  return (
    <div className="flex flex-col bg-white">
      <div className="flex flex-row gap-2 items-center p-2">
        {/* Search Input */}
        <div className="flex w-full max-w-xs flex-col gap-6">
          <InputGroup>
            <InputGroupInput
              ref={searchInputRef}
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setFocusSearchInput(true)}
              onBlur={() => setFocusSearchInput(false)}
            />
            <InputGroupAddon>
              <SearchIcon />
            </InputGroupAddon>
            <InputGroupAddon align="inline-end">
              <Kbd>{modifierKey}</Kbd>
              <Kbd>F</Kbd>
            </InputGroupAddon>
          </InputGroup>
        </div>

        {/* Filter Chips */}
        {filterChips.map((chip) => (
          <GreyChipButton
            key={chip.href}
            href={chip.href}
            label={chip.label}
            iconNode={<chip.icon className="size-3" />}
            active={pathname === chip.href}
          />
        ))}
      </div>
      <KeepAliveOutlet />
    </div>
  );
}
